import React, { useMemo, useState } from 'react';

import { Alert, Input, message } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import useContactGatewayConfigurations from '@hooks/Configurations/UseContactGatewayConfigurations';
import {
    useSendMassiveModelDataUpdateRequest,
    useSendModelDataUpdateReminder,
    useSendModelDataUpdateRequest,
} from '@hooks/API/Contact/UseModelDataUpdateRequests';

import { NwButton, NwCancelButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NwForm, NwFormButtonsBar, NwFormItem } from '@components/Gui/NwForm/NwFormWrapper';
import SignatureSelector from '@components/Gui/NwForm/SignatureSelector';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { TextArea } = Input;

const getSendRequestErrorMessage = (error) => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) return responseData.trim();
    if (responseData && typeof responseData === 'object') {
        const responseMessage =
            responseData.Message ??
            responseData.message ??
            responseData.Error ??
            responseData.error ??
            responseData.title;
        if (responseMessage != null && `${responseMessage}`.trim()) return String(responseMessage).trim();
    }
    return error?.message || 'Request failed';
};

const ModelDataUpdatesSendRequestDrawer = ({
    contactId,
    onClose,
    onSent,
    isReminder = false,
    contacts = [],
    messagePlaceholder = '',
    useDefaultMessage = true,
}) => {
    const [touchForm, setTouchForm] = useState(false);
    const contactGatewayConfigurations = useContactGatewayConfigurations();
    const defaultMessage = useDefaultMessage
        ? isReminder
            ? contactGatewayConfigurations?.ReminderMessage || ''
            : contactGatewayConfigurations?.StandardMessage || ''
        : '';
    const hasMultipleContacts = contacts && Array.isArray(contacts) && contacts.length > 0;
    const contactIdForRequest = Number(contactId);
    const hasValidContactId = Number.isFinite(contactIdForRequest) && contactIdForRequest > 0;
    const noRecipientsError = !hasValidContactId && !hasMultipleContacts;

    const { mutateAsync: sendUpdateRequest } = useSendModelDataUpdateRequest();
    const { mutateAsync: sendMassiveUpdateRequest } = useSendMassiveModelDataUpdateRequest();
    const { mutateAsync: sendUpdateReminder } = useSendModelDataUpdateReminder();

    const initialValues = useMemo(
        () => ({
            Message: defaultMessage,
            SignatureID: null,
        }),
        [defaultMessage]
    );

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            if (isReminder) {
                const contactIds = hasMultipleContacts
                    ? contacts.map((c) => c.ID)
                    : hasValidContactId
                      ? [contactIdForRequest]
                      : [];
                if (contactIds.length === 0) {
                    setSubmitting(false);
                    return;
                }
                await sendUpdateReminder({
                    contactIds,
                    signatureId: values.SignatureID,
                    message: values.Message,
                });
            } else if (hasMultipleContacts) {
                await sendMassiveUpdateRequest({
                    contacts: [...contacts],
                    signatureId: values.SignatureID,
                    message: values.Message,
                });
            } else if (hasValidContactId) {
                await sendUpdateRequest({
                    contactId: contactIdForRequest,
                    signatureId: values.SignatureID,
                    message: values.Message,
                });
            } else {
                setSubmitting(false);
                return;
            }

            if (onSent) {
                await onSent();
            }

            message.success(
                isReminder
                    ? hasMultipleContacts && contacts.length > 1
                        ? 'Update reminders sent.'
                        : 'Update reminder sent.'
                    : hasMultipleContacts && contacts.length > 1
                      ? 'Update data requests sent.'
                      : 'Update data request sent.'
            );
            setTouchForm(false);
            setSubmitting(false);
            onClose();
        } catch (error) {
            message.error(getSendRequestErrorMessage(error));
            setSubmitting(false);
        }
    };

    return (
        <NwDrawer
            title={isReminder ? 'send data update reminder' : 'send data update request'}
            touchForm={touchForm}
            onTouchForm={setTouchForm}
            onClose={onClose}
        >
            <div className="drawer-content standard">
                {hasMultipleContacts ? (
                    <Alert
                        type="info"
                        showIcon
                        message={
                            isReminder
                                ? contacts.length === 1
                                    ? 'The reminder will be sent to 1 recipient.'
                                    : `The reminder will be sent to ${contacts.length} recipients.`
                                : contacts.length === 1
                                  ? 'The request will be sent to 1 recipient.'
                                  : `The request will be sent to ${contacts.length} recipients.`
                        }
                        style={{ marginBottom: '1rem' }}
                    />
                ) : null}
                {noRecipientsError ? (
                    <Alert
                        type="error"
                        showIcon
                        message="No recipient is available. Open this action from a contact or select at least one recipient before sending."
                        style={{ marginBottom: '1rem' }}
                    />
                ) : null}
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={handleSubmit}
                    validationSchema={Yup.object().shape({
                        Message: Yup.string().trim().required('Message is required'),
                    })}
                >
                    {({ errors, isSubmitting, setFieldTouched, setFieldValue, touched, values }) => (
                        <NwForm values={values} onTouchForm={setTouchForm} layout="vertical">
                            <NwFormItem
                                label="Message"
                                required
                                control={
                                    <>
                                        <TextArea
                                            autoComplete="off"
                                            rows={8}
                                            style={{ height: 'calc(100vh - 20rem)', maxHeight: '37.5rem' }}
                                            value={values.Message}
                                            placeholder={messagePlaceholder}
                                            onBlur={() => setFieldTouched('Message', true)}
                                            onChange={(event) => setFieldValue('Message', event.target.value)}
                                        />
                                        {touched.Message && errors.Message ? (
                                            <div className="ant-form-item-explain ant-form-item-explain-error">
                                                {errors.Message}
                                            </div>
                                        ) : null}
                                    </>
                                }
                            />
                            <NwFormItem
                                label="Signature"
                                control={
                                    <SignatureSelector
                                        value={values.SignatureID}
                                        onChange={(signatureId) => setFieldValue('SignatureID', signatureId)}
                                    />
                                }
                            />
                            <NwFormButtonsBar
                                marginTop="2rem"
                                left={
                                    <NwCancelButton
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setTouchForm(false);
                                            onClose();
                                        }}
                                    />
                                }
                                right={
                                    <NwButton
                                        htmlType="submit"
                                        primary
                                        disabled={
                                            isSubmitting ||
                                            (hasMultipleContacts ? contacts.length === 0 : !hasValidContactId)
                                        }
                                        loading={isSubmitting}
                                        loadingLabel="Sending..."
                                        label="Send"
                                        icon={byPrefixAndName.fasl['paper-plane']}
                                    />
                                }
                            />
                        </NwForm>
                    )}
                </Formik>
            </div>
        </NwDrawer>
    );
};

export default ModelDataUpdatesSendRequestDrawer;
